/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
// libraries
import React from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Query } from 'react-apollo';
import qs from 'qs';

import {
    object, string, bool,
} from 'prop-types';
import gql from 'graphql-tag';
import mbpLogger from 'mbp-logger';
// components
import PlantGuidePage from './PlantGuidePage';
import GraphqlProductDetailsPage from './ProductDetailsPage/GraphqlProductDetailsPage';

// helpers, redux, queries
import {
    getUserSubmittedProductFilterZipcode, getLocationType, getIsBot, getCurrencyData,
} from '../../../../state/ducks/App/App-Selectors';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getFeatureFlags } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { GRAPHQL_ENV } from '../../../gql';
import findCategoryPageByURL from '../../../gql/queries/findCategoryPageByURL';
import GraphqlSeoHeadTags from '../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';

const stopReRender = (prevProps, nextProps) => {
    // This component should only run its query a single time on initial category page load.
    // If zipcode is set before cat page the query should still run with this filtering info
    // To prevent this larger query from rerunning on subsequent changes of location type or zipcode
    // The component is wrapped in react.memo and this function is provided as a param.
    // If this function returns true the component does not rerender and the query is not rerun
    if (prevProps.locationType !== nextProps.locationType) return true;
    if (prevProps.userSubmittedZip !== nextProps.userSubmittedZip) return true;
    return false;
};
// memoized to prevent re-renders, stopReRender ^
const GraphqlPlantGuide = React.memo(({
    match: { params: { slug } }, brand, userSubmittedZip, locationType, isBot, currency, history, featureFlags,
}) => {
    const getFacetsFromUrl = () => {
        if (history.location?.search) {
            if (featureFlags['is-facet-throwing-error-resolver-enabled']) {
                try {
                    const decodedString = decodeURI(history?.location?.search).replace('?', '');
                    const queryParams = qs.parse(decodedString);
                    let redirectLocationData = queryParams.facet;
                    if (typeof queryParams.facet === 'string') {
                        redirectLocationData = [queryParams.facet];
                    }
                    const checkedFacetsData = redirectLocationData?.length && redirectLocationData?.[0] !== '' ? redirectLocationData : [];
                    return checkedFacetsData;
                } catch (e) {
                    mbpLogger.logError({
                        message: 'Malformed URI',
                        error: e,
                        module: 'useCheckedFacets',
                        function: 'useCheckedFacets',
                    });
                }
            } else if (history.location?.search) {
                const searchValue = unescape(history.location?.search);
                const locationData = searchValue?.includes('?facet=') ? searchValue.replace('?facet=', '') : '';
                const redirectLocationData = locationData?.split('&');
                const checkedFacetsData = redirectLocationData?.length && redirectLocationData?.[0] !== '' ? redirectLocationData : [];
                return checkedFacetsData;
            }
        }
        return [];
    };
    let PLANT_GUIDE_PAGE_QUERY = {};
    let query = '';
    let selectedFacet = [];
    if (slug) {
        PLANT_GUIDE_PAGE_QUERY = gql`{
            content1: findContent(brand: "${brand.domain}", environment: "${GRAPHQL_ENV}", contentType: "standard_product", query:"{\\"plant_guide_slug\\" : \\"${slug}\\"}")
            { content }
            content2: findContent(brand: "${brand.domain}", environment: "${GRAPHQL_ENV}", contentType: "standard_product", query:"{\\"url\\" : \\"/${slug}\\"}")
            { content }
        }
        `;
        query = 'findContent';
    } else {
        selectedFacet = getFacetsFromUrl();
        PLANT_GUIDE_PAGE_QUERY = findCategoryPageByURL(brand.domain, '/plant-guide', 'standard_collections', userSubmittedZip, locationType, isBot, currency.currencyName, 100);
        query = 'findCategoryPageByURL';
    }
    return (
        <Query query={PLANT_GUIDE_PAGE_QUERY} fetchPolicy="cache-and-network">
            {({ data, error }) => {
                if (error) {
                    mbpLogger.logError({
                        query: PLANT_GUIDE_PAGE_QUERY,
                        component: 'GraphqlPlantGuide.js',
                        message: `Error executing ${query} query`,
                        env: GRAPHQL_ENV,
                        error,
                    });
                    return null;
                }

                if (!data || (slug && (!data?.content1?.content?.entries[0]?.product_base_code && !data?.content2?.content?.entries[0]?.product_base_code))) {
                    mbpLogger.logWarning({
                        query: PLANT_GUIDE_PAGE_QUERY,
                        component: 'GraphqlPlantGuide.js',
                        message: 'No data returned for Plant Guide Page Query',
                        env: GRAPHQL_ENV,
                    });
                    return null;
                }
                let pageData = {};
                let seoData = {};
                if (slug && data?.content1?.content?.entries[0]?.product_base_code) {
                    pageData = data?.content1?.content?.entries[0];
                } else if (slug && data?.content2?.content?.entries[0]?.product_base_code) {
                    pageData = data?.content2?.content?.entries[0];
                } else {
                    pageData = data?.findCategoryPageByUrl;
                    seoData = pageData?.content?.entries?.[0]?.seo || {};
                    if (!seoData?.page_title) seoData.page_title = 'Plant Guides | Plants.com';
                }
                mbpLogger.logInfo({
                    message: 'data returned from collection page query',
                });

                if (slug) {
                    return (
                        <>
                            <GraphqlProductDetailsPage
                                code={pageData?.product_base_code}
                            />
                        </>
                    );
                }

                return (
                    <>
                        <GraphqlSeoHeadTags seoData={seoData} />
                        <PlantGuidePage
                            wikiPageData={pageData}
                            selectedFacet={selectedFacet}
                        />
                    </>
                );
            }}
        </Query>
    );
}, stopReRender);

GraphqlPlantGuide.propTypes = {
    isBot: bool,
    brand: object.isRequired,
    userSubmittedZip: string.isRequired,
    locationType: string.isRequired,
    currency: object.isRequired,
    match: {
        params: {
            slug: string.isRequired,
        },
    },
    featureFlags: object.isRequired,
    history: object.isRequired,
};

GraphqlPlantGuide.defaultProps = {
    isBot: false,
    match: {
        params: {
            slug: '',
        },
    },
};

const mapStateToProps = (state) => ({
    isBot: getIsBot(state),
    brand: getBrand(state),
    userSubmittedZip: getUserSubmittedProductFilterZipcode(state),
    locationType: getLocationType(state),
    currency: getCurrencyData(state),
    featureFlags: getFeatureFlags(state),
});

const enhance = compose(
    withRouter,
    connect(mapStateToProps),
);

export default enhance(GraphqlPlantGuide);
